import React, { useState, useEffect } from "react"
import styled from "styled-components"
import media from "styles/media"
import colors from "styles/colors"
import text from "styles/text"
import StyledLink from "components/StyledLink"
import Checkmark from "../images/Checkmark.svg"
import ExpandMore from "../images/ExpandMore.webp"
import ExpandLess from "../images/ExpandLess.webp"
import gsap from "gsap"

const AccordionSplit = ({ content }) => {
  const [selected, setSelected] = useState(0)
  const animation = gsap.timeline({ paused: true })

  useEffect(() => {
    const targets = gsap.utils.toArray(".accordion-items")

    animation.to(targets[selected], {
      height: "auto",
      duration: 0.8,
      ease: "power1.out",
    })
    animation.to(
      targets.filter((i, index) => selected !== index),
      { height: 0, duration: 0.8, ease: "power1.out" },
      0
    )
    animation.play()
  }, [selected])

  const allSections = content?.sections?.map((item, index) => {
    const current = selected === index
    return (
      <AcoordianPanel key={index}>
        <HeadlineDiv $selected={current} onClick={() => setSelected(index)}>
          <Headline>{item?.headline}</Headline>
          <ExpandedIcon src={current ? ExpandLess : ExpandMore} />
        </HeadlineDiv>
        <Line $selected={selected === index} />
        <AccordianContent>
          <FlexibleDiv className={`accordion-items`}>
            <BodyCopy
              $checkmark={item?.checkmark}
              dangerouslySetInnerHTML={{ __html: item?.bodyCopy }}
            />
            {item?.linkUrl && (
              <StyledLink href={item?.linkUrl}>{item?.linkText}</StyledLink>
            )}
          </FlexibleDiv>
        </AccordianContent>
      </AcoordianPanel>
    )
  })

  return (
    <Wrapper>
      <HeaderDiv>
        <Header>{content?.header} </Header>
        <Body>{content?.body}</Body>
      </HeaderDiv>
      <AccordianWrapper>{allSections}</AccordianWrapper>
    </Wrapper>
  )
}

export default AccordionSplit

const BodyCopy = styled.p`
  p {
    ${text.bodyMd}
  }
  strong {
    ${text.bodyMdBold}
  }
  ul {
    display: flex;
    flex-direction: column;
    list-style-type: none;
    gap: 1.389vw;
    margin: 1.389vw 0;

    ${media.fullWidth} {
      gap: 8px;
      margin: 32px 0px;
    }

    ${media.tablet} {
      gap: 0vw;
      margin: 2.344vw 0px;
    }

    ${media.mobile} {
      gap: 0.935vw;
      margin: 7.477vw 0vw;
    }
  }
  li {
    position: relative;
    display: flex;
    align-items: center;
    left: 2.222vw;
    ${text.bodyMdBold}

    ${media.fullWidth} {
      left: 32px;
    }

    ${media.tablet} {
      height: 5.469vw;
      left: 3.32vw;
    }

    ${media.mobile} {
      height: 12.15vw;
      left: 7.944vw;
    }
  }
  li::before {
    position: absolute;
    content: "";
    display: inline-block;
    background-image: url(${Checkmark});
    background-size: cover;
    background-repeat: no-repeat;
    width: 1.111vw;
    height: 1.111vw;
    left: -2.222vw;
    ${media.fullWidth} {
      width: 16px;
      height: 16px;
      left: -32px;
    }

    ${media.tablet} {
      width: 1.563vw;
      height: 1.563vw;
      left: -3.32vw;
    }

    ${media.mobile} {
      width: 3.738vw;
      height: 3.738vw;
      left: -7.944vw;
    }
  }
`
const ExpandedIcon = styled.img`
  width: 1.667vw;
  height: 1.667vw;
  ${media.fullWidth} {
    width: 24px;
    height: 24px;
  }

  ${media.tablet} {
    width: 24px;
    height: 24px;
  }

  ${media.mobile} {
    width: 5.607vw;
    height: 5.607vw;
  }
`
const Headline = styled.h4`
  ${text.h4};
  cursor: pointer;
`
const HeadlineDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${props =>
    props.$selected ? `${colors.black}` : `${colors.grey400}`};
`
const Line = styled.span`
  position: absolute;
  left: 0vw;
  top: 0vw;
  height: 100%;
  width: 0.347vw;
  border-radius: ${props => (props.$selected ? "3.472vw" : "unset")};
  background-color: ${props =>
    props.$selected ? `${colors.grey500}` : `${colors.grey75}`};
  ${media.fullWidth} {
    left: 0px;
    top: 0px;
    height: 100%;
    width: 5px;
    border-radius: ${props => (props.$selected ? "50px" : "unset")};
  }

  ${media.tablet} {
    left: 0vw;
    top: 0vw;
    height: 100%;
    width: 0.391vw;
    border-radius: ${props => (props.$selected ? "4.883vw" : "unset")};
  }

  ${media.mobile} {
    left: 0vw;
    top: 0vw;
    height: 100%;
    width: 0.935vw;
    border-radius: ${props => (props.$selected ? "11.682vw" : "unset")};
  }
`
const FlexibleDiv = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0vw;
  gap: 1.667vw;
  height: 0;
  ${media.fullWidth} {
    margin: 0px;
    gap: 24px;
  }

  ${media.tablet} {
    gap: 2.344vw;
  }

  ${media.mobile} {
    gap: 7.477vw;
  }
`
const AccordianContent = styled.div`
  position: relative;
  display: flex;
  width: 50.347vw;
  padding-right: 1.389vw;
  ${media.fullWidth} {
    width: 725px;
    padding-right: 20px;
  }

  ${media.tablet} {
    width: 55.664vw;
    padding-right: 1.953vw;
  }

  ${media.mobile} {
    width: 77.57vw;
    padding-right: 4.673vw;
  }
`
const AcoordianPanel = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.944vw;
  width: 50.347vw;
  padding: 0.556vw 0vw 2.083vw 2.778vw;
  ${media.fullWidth} {
    gap: 32px;
    width: 725px;
    padding: 8px 0px 30px 40px;
  }

  ${media.tablet} {
    gap: 3.125vw;
    width: 55.664vw;
    padding: 2.344vw 0vw 2.344vw 4.297vw;
  }

  ${media.mobile} {
    gap: 7.477vw;
    width: 87.85vw;
    padding: 5.607vw 0vw 5.607vw 9.346vw;
  }
`
const AccordianWrapper = styled.div``

const Body = styled.p`
  ${text.bodyMd};

  ${media.mobile} {
    ${text.bodyMd}
  }
`
const Header = styled.h3`
  ${text.h3};
`
const HeaderDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 24.861vw;
  padding: 0vw 2.778vw 0vw 0vw;
  gap: 3.472vw;

  ${media.fullWidth} {
    width: 358px;
    padding: 0px 40px 0px 0px;
    gap: 50px;
  }

  ${media.tablet} {
    width: 29.297vw;
    padding: 2.93vw 1.953vw 0vw 0vw;
    gap: 4.883vw;
  }

  ${media.mobile} {
    padding: 0vw 0vw 7.009vw 0vw;
    width: 87.85vw;
    gap: 11.682vw;
  }
`
const Wrapper = styled.div`
  ${text.h2}
  display: flex;
  gap: 4.861vw;
  width: 83.125vw;

  ${media.fullWidth} {
    gap: 70px;
    width: 1197px;
  }

  ${media.tablet} {
    width: 100vw;
    gap: 4.883vw;
    width: 100vw;
    padding: 60px 40px;
  }

  ${media.mobile} {
    flex-direction: column;
    padding: 0vw 0vw;
    width: 87.85vw;
    gap: 2.336vw;
  }
`
